/**
 * ViewContextPanel — Debug panel for DRR-288 ViewContext
 *
 * Shows current view context state for debugging:
 * - Current lens
 * - Terminology mappings
 * - Visible primitives
 * - Nav groups
 * - Work context
 * - Route constraints
 *
 * Toggle with Ctrl+Shift+V or via the pill in bottom-left.
 */

import { useState, useEffect } from 'react';
import {
  Paper,
  Text,
  Group,
  Badge,
  Stack,
  Code,
  Table,
  Collapse,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Kbd,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconChevronRight,
  IconEye,
  IconLanguage,
  IconCompass,
  IconShield,
  IconX,
  IconFocusCentered,
} from '@tabler/icons-react';

import { useViewContext, useNavGroups, useRouteConstraints } from '@/contexts/ViewContext';

export function ViewContextPanel() {
  const [expanded, setExpanded] = useState(false);
  const ctx = useViewContext();
  const { terminology } = ctx; // Get raw terminology map for display
  const navGroups = useNavGroups();
  const constraints = useRouteConstraints();

  const [showTerminology, { toggle: toggleTerminology }] = useDisclosure(false);
  const [showPrimitives, { toggle: togglePrimitives }] = useDisclosure(false);
  const [showNavGroups, { toggle: toggleNavGroups }] = useDisclosure(false);

  // Keyboard shortcut: Ctrl+Shift+V
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'V') {
        e.preventDefault();
        setExpanded(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Minimized pill
  if (!expanded) {
    return (
      <Tooltip label={<>View Context <Kbd size="xs">Ctrl+Shift+V</Kbd></>}>
        <Paper
          shadow="md"
          radius="xl"
          style={{
            position: 'fixed',
            bottom: 16,
            left: 130, // Offset from PermissionDebugPanel
            zIndex: 1100,
            cursor: 'pointer',
            padding: '6px 14px',
          }}
          onClick={() => setExpanded(true)}
        >
          <Group gap={6}>
            <ThemeIcon size="xs" variant="light" color="violet">
              <IconFocusCentered size={12} />
            </ThemeIcon>
            <Text size="xs" fw={500}>
              {ctx.lens}
            </Text>
          </Group>
        </Paper>
      </Tooltip>
    );
  }

  // Expanded panel
  return (
    <Paper
      shadow="lg"
      radius="md"
      p="md"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        width: 380,
        maxHeight: 'calc(100vh - 100px)',
        zIndex: 1100,
      }}
    >
      <ScrollArea h="100%" mah="calc(100vh - 150px)">
        <Stack gap="sm">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="violet">
                <IconFocusCentered size={14} />
              </ThemeIcon>
              <Text fw={600} size="sm">View Context</Text>
              <Kbd size="xs">Ctrl+Shift+V</Kbd>
            </Group>
            <Group gap="xs">
              <Badge color={ctx.lens === 'ARCHITECT' ? 'violet' : ctx.lens === 'CLINICAL' ? 'blue' : 'green'} size="sm">
                {ctx.lens}
              </Badge>
              <ActionIcon size="sm" variant="subtle" onClick={() => setExpanded(false)}>
                <IconX size={14} />
              </ActionIcon>
            </Group>
          </Group>

          <Divider />

          {ctx.loading ? (
            <Text size="sm" c="dimmed">Loading view context...</Text>
          ) : ctx.error ? (
            <Text size="sm" c="red">Error: {ctx.error.message}</Text>
          ) : (
            <>
              {/* Terminology Section */}
              <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleTerminology}>
                <ActionIcon variant="subtle" size="xs">
                  {showTerminology ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ActionIcon>
                <IconLanguage size={16} />
                <Text size="sm" fw={500}>Terminology ({Object.keys(terminology).length})</Text>
              </Group>
              <Collapse in={showTerminology}>
                <Table fz="xs" striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Key</Table.Th>
                      <Table.Th>Display</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(terminology).slice(0, 10).map(([key, value]) => (
                      <Table.Tr key={key}>
                        <Table.Td><Code fz="xs">{key}</Code></Table.Td>
                        <Table.Td><Text size="xs">{String(value)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    {Object.keys(terminology).length > 10 && (
                      <Table.Tr>
                        <Table.Td colSpan={2}>
                          <Text size="xs" c="dimmed">...and {Object.keys(terminology).length - 10} more</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Collapse>

              {/* Visible Primitives Section */}
              <Group gap="xs" style={{ cursor: 'pointer' }} onClick={togglePrimitives}>
                <ActionIcon variant="subtle" size="xs">
                  {showPrimitives ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ActionIcon>
                <IconEye size={16} />
                <Text size="sm" fw={500}>Visible Primitives ({ctx.visiblePrimitives.length})</Text>
              </Group>
              <Collapse in={showPrimitives}>
                <Group gap={4}>
                  {ctx.visiblePrimitives.map((p) => (
                    <Badge key={p} size="xs" variant="outline">{p}</Badge>
                  ))}
                </Group>
              </Collapse>

              {/* Nav Groups Section */}
              <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleNavGroups}>
                <ActionIcon variant="subtle" size="xs">
                  {showNavGroups ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ActionIcon>
                <IconCompass size={16} />
                <Text size="sm" fw={500}>Nav Groups ({navGroups.length})</Text>
              </Group>
              <Collapse in={showNavGroups}>
                <Stack gap={4}>
                  {navGroups.map((ng) => (
                    <Group key={ng.id} gap="xs">
                      <Badge size="xs" variant="filled">{ng.order}</Badge>
                      <Text size="xs">{ng.label}</Text>
                      <Text size="xs" c="dimmed">({ng.routes.length} routes)</Text>
                    </Group>
                  ))}
                </Stack>
              </Collapse>

              {/* Route Constraints */}
              <Group gap="xs">
                <IconShield size={16} />
                <Text size="sm" fw={500}>Constraints</Text>
                <Badge size="xs" color={constraints.dataTier === 'T1' ? 'red' : 'gray'}>
                  {constraints.dataTier ?? 'T2'}
                </Badge>
                <Badge size="xs" color={constraints.safetyLevel === 'CRITICAL' ? 'red' : constraints.safetyLevel === 'HIGH' ? 'orange' : 'gray'}>
                  {constraints.safetyLevel ?? 'MEDIUM'}
                </Badge>
                {constraints.authTier && (
                  <Badge size="xs" variant="outline">Tier {constraints.authTier}</Badge>
                )}
              </Group>

              {/* Work Context */}
              {ctx.workContext && (
                <>
                  <Divider />
                  <Text size="sm" fw={500}>Work Context</Text>
                  <Code block style={{ fontSize: 10 }}>
                    {JSON.stringify(ctx.workContext, null, 2)}
                  </Code>
                </>
              )}
            </>
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  );
}
